import { cn } from '../../lib/cn';
import { Avatar, AvatarFallback, getInitials } from '../avatar';
import { Button } from '../button';
import { Icon } from '../primitives/Icon';
import type { PresenceStatus } from '../avatars/PresenceIndicator';

export interface UserCardProps {
  name: string;
  role?: string;
  /** Department/team line — appears under the role in the full variant. */
  dept?: string;
  /** Free-form location string. Renders with pin icon. */
  location?: string;
  email?: string;
  phone?: string;
  status?: PresenceStatus;
  /** When true, renders the compact (avatar + name + role) one-row variant. */
  compact?: boolean;
  /** Optional handler for the Message footer button (full variant only). */
  onMessage?: () => void;
  /** Optional handler for the Call footer button (full variant only). */
  onCall?: () => void;
  className?: string;
}

const STATUS_BG: Record<PresenceStatus, string> = {
  online: 'bg-success',
  busy: 'bg-warning',
  away: 'bg-text-muted',
  offline: 'bg-text-muted',
  'on-call': 'bg-accent-500',
};

/**
 * People directory card. Two variants: full (header + body + footer)
 * and compact (avatar + name + role row). Presence dot overlays the
 * avatar's bottom-right corner in both variants.
 */
export function UserCard({
  name,
  role,
  dept,
  location,
  email,
  phone,
  status = 'online',
  compact = false,
  onMessage,
  onCall,
  className,
}: UserCardProps) {
  const initials = getInitials(name);
  const presenceClass = STATUS_BG[status];

  if (compact) {
    return (
      <div
        className={cn(
          'inline-flex items-center gap-3 rounded-md border border-border bg-bg-surface px-3 py-2',
          className,
        )}
      >
        <span className="relative inline-block">
          <Avatar size="sm">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span
            aria-label={status}
            className={cn(
              'absolute bottom-0 right-0 h-2 w-2 rounded-full ring-2 ring-bg-surface',
              presenceClass,
            )}
          />
        </span>
        <div className="flex flex-col">
          <span className="text-body font-semibold text-text-primary">{name}</span>
          {role ? <span className="text-caption text-text-secondary">{role}</span> : null}
        </div>
      </div>
    );
  }

  return (
    <article
      className={cn(
        'flex w-full max-w-sm flex-col gap-4 rounded-lg border border-border bg-bg-surface p-5 shadow-sm',
        className,
      )}
    >
      <header className="flex items-start gap-4">
        <span className="relative inline-block">
          <Avatar size="lg">
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
          <span
            aria-label={status}
            className={cn(
              'absolute bottom-0 right-0 h-3 w-3 rounded-full ring-2 ring-bg-surface',
              presenceClass,
            )}
          />
        </span>
        <div className="flex flex-col gap-0.5">
          <span className="text-h3 text-text-primary">{name}</span>
          {role ? <span className="text-body text-text-secondary">{role}</span> : null}
          {dept ? <span className="text-caption text-text-muted">{dept}</span> : null}
        </div>
      </header>
      {(location || email || phone) ? (
        <div className="flex flex-col gap-2 border-t border-border pt-4 text-caption text-text-secondary">
          {location ? (
            <div className="flex items-center gap-2">
              <Icon name="pin" size={12} />
              {location}
            </div>
          ) : null}
          {email ? (
            <div className="flex items-center gap-2">
              <Icon name="at" size={12} />
              {email}
            </div>
          ) : null}
          {phone ? (
            <div className="flex items-center gap-2">
              <Icon name="phone" size={12} />
              {phone}
            </div>
          ) : null}
        </div>
      ) : null}
      <footer className="flex items-center gap-2 border-t border-border pt-4">
        <Button type="button" variant="secondary" size="sm" onClick={onMessage}>
          <Icon name="chat" size={12} />
          Message
        </Button>
        <Button type="button" variant="secondary" size="sm" onClick={onCall}>
          <Icon name="phone" size={12} />
          Call
        </Button>
      </footer>
    </article>
  );
}
